import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  votosOrfaosApi,
  type ResumoOrfaosBairro,
  type TipoVotoOrfao,
  type VotoOrfao,
} from '@/lib/api/analise';
import { pesquisasApi } from '@/lib/api/pesquisas';
import { useAuthStore } from '@/lib/stores/authStore';
import { useBairros } from '@/lib/hooks/useBairros';
import { useMockStore } from '@/lib/stores/mockStore';
import { toast } from '@/lib/stores/toastStore';
import { Drawer } from '@/design-system/Drawer';
import { Info } from '@/design-system/Info';

const TIPOS: Array<{ v: TipoVotoOrfao; l: string; cor: string }> = [
  { v: 'branco', l: 'Branco', cor: 'neutral' },
  { v: 'nulo', l: 'Nulo', cor: 'warn' },
  { v: 'abstencao', l: 'Abstenção', cor: 'crit' },
  { v: 'desistente', l: 'Base de desistente', cor: 'acc' },
];

const rotuloTipo = (t: TipoVotoOrfao) => TIPOS.find((x) => x.v === t)?.l || t;

/** Percentual com uma casa, no formato do país: 15 → `15,0%`. */
const pct = (v: number) => `${v.toFixed(1).replace('.', ',')}%`;

/** Variação em pontos percentuais, com sinal: 5 → `+5,0 p.p.`. */
const pp = (v: number) => `${v > 0 ? '+' : ''}${v.toFixed(1).replace('.', ',')} p.p.`;

/** Uma linha da série: o mesmo território nos dois ciclos, quando existe. */
interface LinhaSerie {
  bairro: string;
  anterior?: ResumoOrfaosBairro;
  atual?: ResumoOrfaosBairro;
  eleitores: number;
}

/**
 * Junta os dois ciclos pelo nome do território. Territórios que só aparecem em
 * um dos ciclos ficam na tabela — com a ausência declarada, e não com variação
 * calculada contra zero, que fingiria uma queda ou uma alta que ninguém mediu.
 */
function montarSerie(anterior: ResumoOrfaosBairro[], atual: ResumoOrfaosBairro[]): LinhaSerie[] {
  const mapa = new Map<string, LinhaSerie>();
  const registrar = (r: ResumoOrfaosBairro, campo: 'anterior' | 'atual') => {
    const chave = r.bairro.toLowerCase();
    const linha = mapa.get(chave) ?? { bairro: r.bairro, eleitores: 0 };
    linha[campo] = r;
    linha.eleitores = linha.eleitores || r.eleitores;
    mapa.set(chave, linha);
  };
  anterior.forEach((r) => registrar(r, 'anterior'));
  atual.forEach((r) => registrar(r, 'atual'));

  return [...mapa.values()].sort(
    (a, b) =>
      (b.atual?.percentual_eleitorado ?? -1) - (a.atual?.percentual_eleitorado ?? -1) ||
      (b.anterior?.percentual_eleitorado ?? 0) - (a.anterior?.percentual_eleitorado ?? 0),
  );
}

/**
 * Indefinição declarada por uma pesquisa. `eleg.pesquisa.resultados` é um jsonb
 * livre — quem cadastra escolhe os rótulos, e a tela de Pesquisas apenas sugere
 * "Indecisos". Por isso a busca é pelo começo do nome normalizado, e não por uma
 * chave fixa. Brancos e nulos NÃO entram: são outra coisa, e a tela já os mede
 * do lado esquerdo. Sem campo de indefinição, a pesquisa não vira barra zero —
 * ela é contada como pesquisa que não mediu isso.
 */
function indefinicaoDaPesquisa(resultados: Record<string, number>): number | undefined {
  for (const [chave, valor] of Object.entries(resultados)) {
    const normalizada = chave
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();
    if (normalizada.startsWith('indec') || normalizada.startsWith('indefin')) return valor;
  }
  return undefined;
}

export function ElegBnPage() {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  const { bairros } = useBairros();
  const setBairroActiveId = useMockStore((s) => s.setBairroActiveId);

  const [ciclo, setCiclo] = useState(2024);
  const [modal, setModal] = useState<VotoOrfao | 'novo' | null>(null);

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const { data: votos = [], isLoading } = useQuery({
    queryKey: ['votos-orfaos', ciclo],
    queryFn: () => votosOrfaosApi.list(token, ciclo),
    enabled: !!token,
  });

  const { data: resumo = [] } = useQuery({
    queryKey: ['votos-orfaos-resumo', ciclo],
    queryFn: () => votosOrfaosApi.resumo(token, ciclo),
    enabled: !!token,
  });

  // Ciclo municipal anterior. Os ciclos do seletor andam de quatro em quatro
  // anos; a comparação do protótipo (2020 → 2024) é esta, generalizada.
  const anterior = ciclo - 4;

  const { data: resumoAnterior = [] } = useQuery({
    queryKey: ['votos-orfaos-resumo', anterior],
    queryFn: () => votosOrfaosApi.resumo(token, anterior),
    enabled: !!token,
  });

  const { data: pesquisas = [] } = useQuery({
    queryKey: ['pesquisas'],
    queryFn: () => pesquisasApi.list(token),
    enabled: !!token,
  });

  const serie = montarSerie(resumoAnterior, resumo);

  // Só confiança média ou alta, como o protótipo: a coluna `confianca` existe
  // em `eleg.pesquisa` e é o critério real.
  const comparaveis = pesquisas.filter((p) => p.confianca !== 'baixa');
  const indefinicoes = comparaveis
    .map((p) => ({ p, v: indefinicaoDaPesquisa(p.resultados ?? {}) }))
    .filter((x): x is { p: (typeof comparaveis)[number]; v: number } => x.v !== undefined);
  const semIndefinicao = comparaveis.length - indefinicoes.length;
  const maxIndef = Math.max(1, ...indefinicoes.map((x) => x.v));

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['votos-orfaos'] });
    queryClient.invalidateQueries({ queryKey: ['votos-orfaos-resumo'] });
  };

  const criar = useMutation({
    mutationFn: (dados: Partial<VotoOrfao>) => votosOrfaosApi.create(dados, token),
    onSuccess: () => {
      invalidar();
      toast('Registro cadastrado na inteligência eleitoral.');
      setModal(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao cadastrar o registro.')),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<VotoOrfao> }) =>
      votosOrfaosApi.update(id, dados, token),
    onSuccess: () => {
      invalidar();
      toast('Registro atualizado.');
      setModal(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar o registro.')),
  });

  const excluir = useMutation({
    mutationFn: (id: string) => votosOrfaosApi.delete(id, token),
    onSuccess: () => {
      invalidar();
      toast('Registro removido.');
      setModal(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao remover o registro.')),
  });

  const total = votos.reduce((a, v) => a + v.votos, 0);
  const porTipo = (t: TipoVotoOrfao) =>
    votos.filter((v) => v.tipo === t).reduce((a, v) => a + v.votos, 0);
  const maxBairro = Math.max(1, ...resumo.map((r) => r.total));

  return (
    <div className="page" id="p-brancos-nulos">
      <div className="head">
        <div>
          <h1 style={{ color: 'var(--eleg)' }}>Brancos, nulos e indefinição</h1>
          <p>
            Brancos, nulos, abstenções e bases de candidatos que deixaram a disputa: o eleitorado
            disputável de cada território, com a evolução entre ciclos. A indefinição vem das
            pesquisas, quando houver.
          </p>
        </div>
        <div className="act">
          <select
            className="sel"
            aria-label="Ciclo"
            value={ciclo}
            onChange={(e) => setCiclo(Number(e.target.value))}
          >
            {[2016, 2020, 2024, 2026].map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <button className="btn pri" onClick={() => setModal('novo')}>
            + Novo registro
          </button>
        </div>
      </div>

      <div className="note" style={{ marginBottom: 12, fontSize: 12 }}>
        <b>Origem do dado:</b> cadastro do gabinete, não carga do TSE.{' '}
        <Info titulo="Por que o número não vem da apuração oficial">
          A apuração oficial não separa "voto disputável" dessa forma. Quem qualifica cada bolsão é
          a equipe.
        </Info>
      </div>

      {/* KPIs */}
      <div className="grid g4">
        <div className="card kpi">
          <div className="l">Total sem destino</div>
          <div className="v" style={{ color: 'var(--eleg)' }}>
            {total.toLocaleString('pt-BR')}
          </div>
          <div className="d">votos mapeados no ciclo {ciclo}</div>
        </div>
        {TIPOS.slice(0, 3).map((t) => (
          <div className="card kpi" key={t.v}>
            <div className="l">{t.l}</div>
            <div className="v">{porTipo(t.v).toLocaleString('pt-BR')}</div>
            <div className="d">
              {total > 0 ? Math.round((porTipo(t.v) / total) * 100) : 0}% do total mapeado
            </div>
          </div>
        ))}
      </div>

      {/* Série entre ciclos + indefinição declarada pelas pesquisas */}
      <div className="grid g21" style={{ marginTop: 12 }}>
        <div className="card flat tw">
          <h2 style={{ padding: '0 0 8px' }}>Brancos e nulos entre ciclos</h2>
          <table aria-label={`Brancos e nulos entre ciclos ${anterior} e ${ciclo}`}>
            <thead>
              <tr>
                <th>Território</th>
                <th>{anterior}</th>
                <th>{ciclo}</th>
                <th>Variação</th>
                <th>Eleitores</th>
                <th>Votos "em disputa"</th>
                <th>Leitura</th>
              </tr>
            </thead>
            <tbody>
              {serie.map((l) => {
                const a = l.anterior?.percentual_eleitorado;
                const n = l.atual?.percentual_eleitorado;
                const variacao = a !== undefined && n !== undefined ? n - a : undefined;
                // Sem os dois ciclos não há variação: nem zero, nem seta.
                const leitura = !l.anterior
                  ? { t: `sem registro em ${anterior}`, c: 'neutral' }
                  : !l.atual
                    ? { t: `sem registro em ${ciclo}`, c: 'neutral' }
                    : variacao! > 0
                      ? { t: 'em alta', c: 'crit' }
                      : variacao! < 0
                        ? { t: 'em queda', c: 'good' }
                        : { t: 'estável', c: 'neutral' };
                return (
                  <tr key={l.bairro} className="row">
                    <td>
                      <Link
                        to="/territorio"
                        onClick={() => {
                          const b = bairros.find((x) => x.n === l.bairro);
                          if (b) setBairroActiveId(b.id);
                        }}
                        style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                      >
                        {l.bairro}
                      </Link>
                    </td>
                    <td className="num">{a !== undefined ? pct(a) : '—'}</td>
                    <td className="num">{n !== undefined ? pct(n) : '—'}</td>
                    <td
                      className="num"
                      style={{
                        color:
                          variacao === undefined || variacao === 0
                            ? 'inherit'
                            : variacao > 0
                              ? 'var(--crit)'
                              : 'var(--good)',
                      }}
                    >
                      {variacao !== undefined ? pp(variacao) : '—'}
                    </td>
                    <td className="num">
                      {l.eleitores > 0 ? l.eleitores.toLocaleString('pt-BR') : '—'}
                    </td>
                    {/* O total registrado, não `eleitores × percentual`: aqui o
                        absoluto é o dado primário e o percentual é o derivado. */}
                    <td className="num">
                      {l.atual ? l.atual.total.toLocaleString('pt-BR') : '—'}
                    </td>
                    <td>
                      <span className={`chip ${leitura.c}`}>{leitura.t}</span>
                    </td>
                  </tr>
                );
              })}
              {serie.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty">
                    Nenhum registro em {anterior} nem em {ciclo}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <section className="card" aria-label="Indefinição por pesquisa">
          <h2>Indefinição por pesquisa</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
            {indefinicoes.map(({ p, v }) => (
              <div key={p.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span>
                    {p.instituto} · {p.periodo}
                  </span>
                  <b className="num">{v}%</b>
                </div>
                <div className="bar" style={{ marginTop: 4 }}>
                  <i style={{ width: `${(v / maxIndef) * 100}%`, background: 'var(--eleg)' }} />
                </div>
              </div>
            ))}
            {indefinicoes.length === 0 && (
              <div className="empty">Nenhuma pesquisa comparável registra indefinição.</div>
            )}
          </div>
          <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '10px 0 0' }}>
            Só pesquisas de confiança média ou alta.
          </p>
          {semIndefinicao > 0 && (
            <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '6px 0 0' }}>
              {semIndefinicao} pesquisa{semIndefinicao > 1 ? 's' : ''} de confiança média ou alta
              sem indefinição registrada.{' '}
              <Info titulo="Por que uma pesquisa pode não registrar indefinição">
                O campo de resultados é livre e nem toda pesquisa publica indecisos. Brancos e
                nulos não valem como substituto.
              </Info>
            </p>
          )}
        </section>
      </div>

      <div className="note warn" style={{ marginTop: 12, fontSize: 12 }}>
        <b>A série acima é do cadastro do gabinete, não do TSE.</b> A apuração pública é agregada
        por zona eleitoral e o sistema não tem a correspondência entre zona e território.{' '}
        <Info titulo="Por que a apuração pública não abre por território">
          A apuração traz brancos e nulos reais de cada ciclo em <code>eleg.detalhe_zona</code>,
          mas só por zona. Sem a correspondência com o território, abrir esses números por bairro
          seria ratear por conta própria.
        </Info>{' '}
        {bairros.length > 0 &&
          `${resumo.length} dos ${bairros.length} territórios do mandato têm registro no ciclo ${ciclo}; os demais aparecem vazios porque ninguém os registrou, não porque não haja brancos e nulos lá.`}
      </div>

      <div className="grid g21" style={{ marginTop: 12 }}>
        {/* Consolidado por território */}
        <div className="card">
          <h2>Concentração por território</h2>
          <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '4px 0 12px' }}>
            Percentual calculado sobre o eleitorado cadastrado de cada território.
          </p>

          {resumo.length === 0 ? (
            <div className="empty">Nenhum registro no ciclo {ciclo}.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {resumo.map((r) => (
                <div key={r.bairro}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <Link
                      to="/territorio"
                      onClick={() => {
                        const b = bairros.find((x) => x.n === r.bairro);
                        if (b) setBairroActiveId(b.id);
                      }}
                      style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                    >
                      <b>{r.bairro}</b>
                    </Link>
                    <span>
                      <b className="num">{r.total.toLocaleString('pt-BR')}</b>
                      <span style={{ color: 'var(--ink3)' }}>
                        {' '}
                        · {r.percentual_eleitorado}% do eleitorado
                      </span>
                    </span>
                  </div>
                  <div className="bar" style={{ marginTop: 4 }}>
                    <i style={{ width: `${(r.total / maxBairro) * 100}%`, background: 'var(--eleg)' }} />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 3 }}>
                    {TIPOS.filter((t) => r.por_tipo[t.v] > 0)
                      .map((t) => `${t.l}: ${r.por_tipo[t.v].toLocaleString('pt-BR')}`)
                      .join(' · ')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Registros */}
        <div className="card flat tw">
          <table>
            <thead>
              <tr>
                <th>Território</th>
                <th>Tipo</th>
                <th style={{ textAlign: 'right' }}>Votos</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {votos.map((v) => (
                <tr key={v.id} className="row">
                  <td>
                    <b>{v.bairro}</b>
                    {v.candidato && (
                      <div style={{ fontSize: 11, color: 'var(--ink3)' }}>{v.candidato}</div>
                    )}
                    {v.observacao && (
                      <div style={{ fontSize: 11, color: 'var(--ink3)' }}>{v.observacao}</div>
                    )}
                  </td>
                  <td>
                    <span className={`chip ${TIPOS.find((t) => t.v === v.tipo)?.cor}`}>
                      {rotuloTipo(v.tipo)}
                    </span>
                  </td>
                  <td className="num" style={{ textAlign: 'right' }}>
                    {v.votos.toLocaleString('pt-BR')}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn sm" onClick={() => setModal(v)}>
                      ✏️
                    </button>{' '}
                    <button
                      className="btn sm"
                      style={{ color: 'var(--crit)' }}
                      disabled={excluir.isPending}
                      onClick={() => {
                        if (window.confirm(`Remover o registro de ${v.bairro}?`)) {
                          excluir.mutate(v.id);
                        }
                      }}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}

              {isLoading && (
                <tr>
                  <td colSpan={4} className="empty">
                    Carregando registros...
                  </td>
                </tr>
              )}
              {!isLoading && votos.length === 0 && (
                <tr>
                  <td colSpan={4} className="empty">
                    Nenhum registro no ciclo {ciclo}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <ModalVoto
          voto={modal === 'novo' ? null : modal}
          ciclo={ciclo}
          bairros={bairros.map((b) => b.n)}
          salvando={criar.isPending || atualizar.isPending}
          onCancelar={() => setModal(null)}
          onSalvar={(dados) => {
            if (modal === 'novo') criar.mutate(dados);
            else atualizar.mutate({ id: modal.id, dados });
          }}
        />
      )}
    </div>
  );
}

function ModalVoto({
  voto,
  ciclo,
  bairros,
  salvando,
  onCancelar,
  onSalvar,
}: {
  voto: VotoOrfao | null;
  ciclo: number;
  bairros: string[];
  salvando: boolean;
  onCancelar: () => void;
  onSalvar: (dados: Partial<VotoOrfao>) => void;
}) {
  const [bairro, setBairro] = useState(voto?.bairro || bairros[0] || '');
  const [tipo, setTipo] = useState<TipoVotoOrfao>(voto?.tipo || 'nulo');
  const [votos, setVotos] = useState(voto?.votos ?? 0);
  const [candidato, setCandidato] = useState(voto?.candidato || '');
  const [observacao, setObservacao] = useState(voto?.observacao || '');

  const submeter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!bairro) {
      toast('Selecione o território.');
      return;
    }
    if (votos < 0) {
      toast('O número de votos não pode ser negativo.');
      return;
    }
    onSalvar({
      ciclo: voto?.ciclo ?? ciclo,
      escopo: voto?.escopo ?? 'Municipal',
      bairro,
      tipo,
      votos,
      candidato: tipo === 'desistente' ? candidato.trim() || undefined : undefined,
      observacao: observacao.trim() || undefined,
    });
  };

  return (
    <form onSubmit={submeter}>
      <Drawer
        aberto
        titulo={voto ? 'Editar registro' : `Novo registro · ciclo ${ciclo}`}
        aoFechar={onCancelar}
        rodape={
          <>
            <button type="button" className="btn" onClick={onCancelar}>
              Cancelar
            </button>
            <button type="submit" className="btn pri" disabled={salvando}>
              {salvando ? 'Salvando...' : voto ? 'Salvar alterações' : 'Cadastrar'}
            </button>
          </>
        }
      >
        <div className="grid g2">
          <div className="field">
            <label>Território *</label>
            <select className="in" value={bairro} onChange={(e) => setBairro(e.target.value)}>
              {bairros.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Tipo</label>
            <select
              className="in"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoVotoOrfao)}
            >
              {TIPOS.map((t) => (
                <option key={t.v} value={t.v}>
                  {t.l}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="field">
          <label>Votos</label>
          <input
            type="number"
            min={0}
            className="in num"
            value={votos}
            onChange={(e) => setVotos(Number(e.target.value) || 0)}
          />
        </div>

        {tipo === 'desistente' && (
          <div className="field">
            <label>Candidato que deixou a disputa</label>
            <input
              className="in"
              value={candidato}
              onChange={(e) => setCandidato(e.target.value)}
              placeholder="Nome do ex-candidato"
            />
          </div>
        )}

        <div className="field">
          <label>Observação</label>
          <textarea
            className="in"
            style={{ minHeight: 60 }}
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            placeholder="Leitura da equipe sobre este bolsão de votos"
          />
        </div>

      </Drawer>
    </form>
  );
}
